import type { ReactNode } from 'react';
import { productContextLabels, type CheckoutContext } from '../drawer/checkout_context.js';
import type { CheckoutMotiveCopy } from '../model/checkout_copy.js';
import type { CheckoutEnv } from '../model/checkout_env.js';
import type { CheckoutItemSource } from '../model/checkout_source.js';
import { flightCheckoutContext } from './flight/checkout_context.js';
import { BusItemDetailExpanded } from './bus/item_detail.js';
import { FlightSummaryLegs } from './flight/summary_legs.js';
import { HotelItemDetailExpanded } from './hotel/item_detail.js';
import { MobilityItemDetailExpanded } from './mobility/item_detail.js';
import { RentalItemDetailCompact } from './rental/item_detail.js';
import { itemsOf } from './items_of.js';

/** O que a tela cheia mostra de cada produto: o contexto do chrome (chip e
 *  título), o resumo em "Sua viagem", como ele pergunta o motivo e o rótulo de
 *  cada linha de preço do carrinho com mais de um item. */
export interface CheckoutScreenComposition {
  readonly context: CheckoutContext;
  readonly summary: ReactNode;
  readonly motive: CheckoutMotiveCopy;
  /** Rótulos na ordem dos itens. */
  readonly lineLabels: () => string[];
}

/** A tela cheia de cada produto: o ÚNICO lugar do fluxo da tela que escolhe
 *  pelo `productType`. O carrinho é de um produto só (o 1º item manda); sem
 *  itens (o pedido à VOLL, que traz o próprio chrome), as chaves do voo. */
export function checkoutScreenComposition(
  items: readonly CheckoutItemSource[],
  env: Pick<CheckoutEnv, 'copy' | 'locale' | 'cityOf'>,
): CheckoutScreenComposition {
  const { copy, locale } = env;
  const tripMotive = copy.motive.trip;
  const [first] = items;
  if (!first)
    return {
      context: productContextLabels(copy, 'flight'),
      summary: null,
      motive: tripMotive,
      lineLabels: () => [],
    };
  switch (first.productType) {
    case 'flight': {
      const flights = itemsOf(items, 'flight');
      return {
        context: flightCheckoutContext(flights, env),
        motive: tripMotive,
        summary: <FlightSummaryLegs items={flights} locale={locale} />,
        // Multi-trecho: cada linha é a sua rota.
        lineLabels: () =>
          flights.map(({ detail }) => `${detail.outbound.from}→${detail.outbound.to}`),
      };
    }
    case 'hotel': {
      const stays = itemsOf(items, 'hotel');
      return {
        context: productContextLabels(copy, 'hotel'),
        motive: tripMotive,
        summary: stays.map((stay) => (
          <HotelItemDetailExpanded key={stay.offerId} detail={stay.detail} />
        )),
        // O id da oferta é um token opaco, mas é o que sobra para rotular a linha.
        lineLabels: () => stays.map((stay) => stay.offerId),
      };
    }
    case 'bus': {
      const legs = itemsOf(items, 'bus');
      return {
        context: productContextLabels(copy, 'bus'),
        motive: tripMotive,
        summary: legs.map((item) => (
          <BusItemDetailExpanded key={item.offerId} detail={item.detail} price={item.price} />
        )),
        // Ida e volta: a perna; trecho único: as rodoviárias.
        lineLabels: () =>
          legs.map(({ detail: { leg, departure, arrival } }) =>
            leg ? copy.bus.leg[leg] : `${departure.stopName} → ${arrival.stopName}`,
          ),
      };
    }
    case 'rental': {
      // Sem layout expandido próprio: o detalhe do card.
      const cars = itemsOf(items, 'rental');
      return {
        context: productContextLabels(copy, 'rental'),
        motive: tripMotive,
        summary: cars.map((car) => (
          <RentalItemDetailCompact key={car.offerId} detail={car.detail} price={car.price} />
        )),
        lineLabels: () => cars.map((car) => car.offerId),
      };
    }
    case 'mobility': {
      const rides = itemsOf(items, 'mobility');
      return {
        context: productContextLabels(copy, 'mobility'),
        motive: copy.motive.ride,
        summary: rides.map((ride) => (
          <MobilityItemDetailExpanded key={ride.offerId} detail={ride.detail} price={ride.price} />
        )),
        lineLabels: () => rides.map((ride) => ride.offerId),
      };
    }
    default: {
      const exhaustive: never = first;
      return exhaustive;
    }
  }
}
